import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getLocale, shallowEqual, useDispatch, useSelector } from 'umi';
import { LANGUAGE_EN } from '../../_config/constant';
import { localeKeyMap } from '../../utils/utils';
import { useNewSelectorParams } from '../../utils/useNewSelectorParams';
import { getSelectorMap } from '../../_config/filterListMap';

type TData = { code: string; name_ch: string; name_en: string; sn: string; deviation: string; };

// 将all移动到第一位
const  moveAllToFront = (data: TData []): TData [] => {
  const allItem = data?.find(item => item?.code?.toLocaleLowerCase() === 'all');
  if (allItem) {
    const newData = data?.filter(item => item?.code?.toLocaleLowerCase() !== 'all');
    return [allItem, ...(newData || [])];
  }
  return data;
};

const formatPaymentTypeData = (data: TData []): TData [] => {
  const  result =  data?.map(item=>{
    if(item?.code?.toLocaleLowerCase() === 'all') {
      item.name_ch = '全部';
    }
    return item;
  });
  return result;
};

export const useFilterData = (path:string,defaultInputList?:any, api?:string, otherParams?:any) => {
  const  {loading, getSelectorList} = useNewSelectorParams({
    realPath: path,
    pageDefaultSelector: defaultInputList,
    isUseFrontFilterConfig: true,
    api: api || '/api/v1/dashboard/new_selector',
    otherParams,
  });

  const selectorRef = useRef<any>({});
  const dispatch = useDispatch();
  const gameCode = useSelector(
    (state: any) => state.dashboard?.currentGame?.game_code,
    shallowEqual,
  );
  // 用 ref 存储快照，useEffect 仅 mount 时执行一次，切游戏时页面整体重新挂载
  const gameCodeRef = useRef(gameCode);
  gameCodeRef.current = gameCode;

  useEffect(() => {
    getSelectorList().then((data: any)=>{
      const categoryList = data?.category_list || [];
      const productList = categoryList?.map((item: any) => item?.product_set || [])?.flat() || [];
      const paymentTypeData = data?.payment_type ? moveAllToFront(formatPaymentTypeData(data?.payment_type || [])) : [];
      selectorRef.current = {
        ...(data || {}),
        category_list: categoryList,
        product_list: productList,
        ...(paymentTypeData?.length > 0 ? { payment_type: paymentTypeData } : {}),
        gameCode: gameCodeRef.current,
      };

      dispatch({
        type: 'dashboardSetting/save',
        payload: {
          selector: selectorRef.current,
          selectorMap: getSelectorMap(selectorRef.current)
        },
      });
    }).catch((e)=>{
      console.log(e);
    });
  }, []);


  return {
    loading,
    selector: selectorRef.current
  };
};

// 筛选器数据中英文翻译
export const usePcRevenueSelectorMap = (selectorVal: any) => {
  const locale = getLocale();
  const { selectorMap, ...selector } = selectorVal || {};

  // 筛选器中英文翻译
  const getPcRevenueSelectorText = useCallback((regionDataType: string, key: string) => {
    const isEn = locale === LANGUAGE_EN;
    if (localeKeyMap[key?.toLowerCase()]) {
      return isEn ? key : localeKeyMap[key.toLowerCase()];
    }
    if (!selector) return key;
    const localeMap = selectorMap?.nameEnMap;
    if (!localeMap || isEn) {
      return key;
    }
    return localeMap?.[regionDataType]?.[key] || key;
  }, [selector, locale]);

  const getCodeText = useCallback((type: string, key: string) => {
    const isEn = locale === LANGUAGE_EN;
    if (localeKeyMap[key?.toLowerCase()]) {
      return isEn ? key : localeKeyMap[key.toLowerCase()];
    }
    if (!selector) return key;
    const localeMap = selectorMap?.nameCnMap;
    if (!localeMap) {
      return key;
    }

    if (isEn) {
      return localeMap?.[type]?.[key]?.en || key;
    } 
    return localeMap?.[type]?.[key]?.cn || localeMap?.[type]?.[key]?.en || key;
    
  }, [selector, locale]);
  
  const countryNameCodeMap:any = useMemo(()=>{
    const countryNameCodeMap = {};
    const countryList = selector?.country_list || [];
    countryList?.forEach((item:any)=>{
      // @ts-ignore
      countryNameCodeMap[item?.country_name_en] = item?.country_code;
    });
    return countryNameCodeMap;
  },[selector]);

  // 根据国家名称获取国家code，用于获取国家国旗展示在表格中
  const getCountryCode = (countryName:string)=>countryNameCodeMap?.[countryName] || '';

  return {
    getPcRevenueSelectorText,
    getCodeText,
    getCountryCode,
    locale,
  };
};
